import React, {useState, useEffect} from 'react';
import {Link as RouterLink} from 'react-router';
import {useLogto} from '@logto/react';
import {
    AppBar as MuiAppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Button,
    Avatar,
    Switch,
    useMediaQuery,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Tooltip,
    Container,
    Backdrop,
    Paper,
} from '@mui/material';
import {
    Menu as MenuIcon,
    School as SchoolIcon,
    Dashboard as DashboardIcon,
    EmojiEvents as EmojiEventsIcon,
    Analytics as AnalyticsIcon,
    Brightness4 as DarkModeIcon,
    Brightness7 as LightModeIcon,
    AccountCircle,
    Home as HomeIcon,
    Logout as LogoutIcon,
    Login as LoginIcon,
    Edit as EditIcon,
    SportsEsports as SportsEsportsIcon,
    AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

import UserSetup from './UserSetup';
import {createApiClient} from '../services/ApiClient';

const AppBar = ({darkMode, setDarkMode, claims, accessToken}) => {
    const {signOut, signIn} = useLogto();
    const isMobile = useMediaQuery('(max-width:900px)');
    const baseUrl = window.location.origin;

    // State for user menu and drawer
    const [anchorEl, setAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [showProfileEdit, setShowProfileEdit] = useState(false);
    const [backendUser, setBackendUser] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showAccessDenied, setShowAccessDenied] = useState(false);
    const [countdown, setCountdown] = useState(10);

    // Check if user is authenticated based on claims
    const isAuthenticated = Boolean(claims && Object.keys(claims).length > 0);

    // Extract user information from claims
    useEffect(() => {
        const extractUserInfo = async () => {
            try {
                if (isAuthenticated) {
                    setUserInfo({
                        name: claims.name || claims.username || 'User',
                        email: claims.email || '',
                        picture: claims.picture || null
                    });
                } else {
                    setUserInfo(null);
                }
            } catch (error) {
                console.error("Failed to fetch user info:", error);
                setUserInfo(null);
            }
            console.log(claims)
        };

        extractUserInfo();
    }, [claims, isAuthenticated]);

    // Fetch backend user data when access token is available
    useEffect(() => {
        const fetchBackendUser = async () => {
            if (!accessToken || !isAuthenticated) return;

            try {
                const apiClient = createApiClient(accessToken);
                const userData = await apiClient.get('/api/users/me');
                setBackendUser(userData);

                // Check if onboarding is needed
                if (userData && !userData.onboarding_complete) {
                    setShowOnboarding(true);
                }
            } catch (error) {
                console.error('Failed to fetch backend user data:', error);

                // Check if it's a 403 error (non-HdM user)
                if (error.status === 403) {
                    setShowAccessDenied(true);
                }
            }
        };

        fetchBackendUser();
    }, [accessToken, isAuthenticated]);

    // Countdown timer for access denied
    useEffect(() => {
        if (showAccessDenied && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (showAccessDenied && countdown === 0) {
            // Sign out when countdown reaches 0
            signOut(`${baseUrl}/info`);
        }
    }, [showAccessDenied, countdown, signOut, baseUrl]);

    // Get user roles from claims
    const isTeacher = isAuthenticated && claims?.roles?.includes('teacher');
    const isStudent = isAuthenticated && claims?.roles?.includes('student');
    const isAdmin = isAuthenticated && (claims?.roles?.includes('admin') || claims?.roles?.includes('administrator'));

    // Check if user is admin-only (no other roles)
    const isAdminOnly = isAdmin && !isTeacher && !isStudent;

    // Get initial for avatar
    const getInitial = () => {
        if (!userInfo || !userInfo.name) return "?";

        const nameParts = userInfo.name.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
        }
        return nameParts[0][0].toUpperCase();
    };

    // Handle user menu open
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle user menu close
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Handle logout
    const handleLogout = () => {
        handleMenuClose();
        signOut(`${baseUrl}/info`);
    };

    // Handle login
    const handleLogin = () => {
        signIn(`${baseUrl}/callback`);
    };

    // Handle profile edit
    const handleEditProfile = () => {
        handleMenuClose();
        setShowProfileEdit(true);
    };

    // Handle profile update completion
    const handleProfileUpdateComplete = (updatedUser) => {
        setBackendUser(updatedUser);
        setShowProfileEdit(false);

        // Update local userInfo state with new username
        if (updatedUser.username) {
            setUserInfo(prev => ({
                ...prev,
                name: updatedUser.username
            }));
        }
    };

    // Handle onboarding completion
    const handleOnboardingComplete = (updatedUser) => {
        setBackendUser(updatedUser);
        setShowOnboarding(false);

        // Update local userInfo state with new username
        if (updatedUser.username) {
            setUserInfo(prev => ({
                ...prev,
                name: updatedUser.username
            }));
        }
    };

    // Toggle dark mode
    const handleToggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    // Toggle drawer
    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    // Get dashboard link based on user role (admin takes priority)
    const getDashboardLink = () => {
        if (!isAuthenticated) return '/login';
        if (isAdmin) return '/admin/dashboard';
        return isTeacher ? '/teacher/dashboard' : '/student/dashboard';
    };

    // Drawer content
    const drawerContent = (
        <Box
            sx={{width: 250}}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
        >
            <Box sx={{p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <SchoolIcon sx={{mr: 1}}/>
                <Typography variant="h6" color="primary" sx={{fontWeight: 700}}>
                    DuoQuanto
                </Typography>
            </Box>
            <Divider/>
            {isAuthenticated && (
                <List>
                    <ListItem button component={RouterLink} to={getDashboardLink()}>
                        <ListItemIcon>
                            <DashboardIcon/>
                        </ListItemIcon>
                        <ListItemText primary="Dashboard"/>
                    </ListItem>

                    {/* Admin-only: show only admin option */}
                    {isAdminOnly && (
                        <ListItem button component={RouterLink} to="/admin/dashboard">
                            <ListItemIcon>
                                <AdminIcon/>
                            </ListItemIcon>
                            <ListItemText primary="Administration"/>
                        </ListItem>
                    )}

                    {/* Teacher navigation */}
                    {isTeacher && (
                        <ListItem button component={RouterLink} to="/teacher/analytics">
                            <ListItemIcon>
                                <AnalyticsIcon/>
                            </ListItemIcon>
                            <ListItemText primary="Analytics"/>
                        </ListItem>
                    )}

                    {/* Student navigation (if not teacher and not admin-only) */}
                    {!isTeacher && !isAdminOnly && (
                        <>
                            <ListItem button component={RouterLink} to="/student/progress">
                                <ListItemIcon>
                                    <EmojiEventsIcon/>
                                </ListItemIcon>
                                <ListItemText primary="My Progress"/>
                            </ListItem>
                            <ListItem button component={RouterLink} to="/student/minigame">
                                <ListItemIcon>
                                    <SportsEsportsIcon/>
                                </ListItemIcon>
                                <ListItemText primary="Minigame"/>
                            </ListItem>
                        </>
                    )}

                    {/* Admin navigation (always last, only if not admin-only) */}
                    {isAdmin && !isAdminOnly && (
                        <ListItem button component={RouterLink} to="/admin/dashboard">
                            <ListItemIcon>
                                <AdminIcon/>
                            </ListItemIcon>
                            <ListItemText primary="Administration"/>
                        </ListItem>
                    )}
                </List>
            )}
            <Divider/>
            <List>
                <ListItem>
                    <ListItemIcon>
                        {darkMode ? <DarkModeIcon/> : <LightModeIcon/>}
                    </ListItemIcon>
                    <ListItemText primary="Dark Mode"/>
                    <Switch
                        checked={darkMode}
                        onChange={handleToggleDarkMode}
                        color="primary"
                    />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <>
            <MuiAppBar
                position="fixed"
                elevation={2}
                sx={{
                    borderRadius: 0,
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
            >
                <Container maxWidth="xl" disableGutters>
                    <Toolbar sx={{position: 'relative', display: 'flex', justifyContent: 'space-between'}}>
                        {/* Left section: Menu icon and logo */}
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            {isAuthenticated && isMobile && (
                                <IconButton
                                    size="large"
                                    edge="start"
                                    color="inherit"
                                    aria-label="menu"
                                    sx={{mr: 2}}
                                    onClick={toggleDrawer(true)}
                                >
                                    <MenuIcon/>
                                </IconButton>
                            )}

                            <Box sx={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                <SchoolIcon sx={{mr: 1, fontSize: '2rem'}}/>
                                <Typography
                                    variant="h6"
                                    component={RouterLink}
                                    to={isAuthenticated ? getDashboardLink() : '/'}
                                    sx={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        letterSpacing: 1.2,
                                        fontSize: '1.6rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    DuoQuanto
                                </Typography>
                            </Box>
                        </Box>

                        {/* Center section: Navigation */}
                        {isAuthenticated && !isMobile && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                pointerEvents: 'none'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    pointerEvents: 'auto'
                                }}>
                                    {/* Admin-only: show only admin button */}
                                    {isAdminOnly && (
                                        <Button
                                            color="inherit"
                                            component={RouterLink}
                                            to="/admin/dashboard"
                                            startIcon={<AdminIcon/>}
                                        >
                                            Admin
                                        </Button>
                                    )}

                                    {/* Teacher navigation */}
                                    {isTeacher && (
                                        <>
                                            <Button
                                                color="inherit"
                                                component={RouterLink}
                                                to="/teacher/dashboard"
                                                startIcon={<DashboardIcon/>}
                                            >
                                                Dashboard
                                            </Button>
                                            <Button
                                                color="inherit"
                                                component={RouterLink}
                                                to="/teacher/analytics"
                                                startIcon={<AnalyticsIcon/>}
                                            >
                                                Analytics
                                            </Button>
                                        </>
                                    )}

                                    {/* Student navigation (if not teacher and not admin-only) */}
                                    {!isTeacher && !isAdminOnly && (
                                        <>
                                            <Button
                                                color="inherit"
                                                component={RouterLink}
                                                to="/student/dashboard"
                                                startIcon={<DashboardIcon/>}
                                            >
                                                Dashboard
                                            </Button>
                                            <Button
                                                color="inherit"
                                                component={RouterLink}
                                                to="/student/progress"
                                                startIcon={<EmojiEventsIcon/>}
                                            >
                                                Stats
                                            </Button>
                                            <Button
                                                color="inherit"
                                                component={RouterLink}
                                                to="/student/minigame"
                                                startIcon={<SportsEsportsIcon/>}
                                            >
                                                Minigame
                                            </Button>
                                        </>
                                    )}

                                    {/* Admin navigation (always last, only if not admin-only) */}
                                    {isAdmin && !isAdminOnly && (
                                        <Button
                                            color="inherit"
                                            component={RouterLink}
                                            to="/admin/dashboard"
                                            startIcon={<AdminIcon/>}
                                        >
                                            Admin
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Right section: Theme toggle and user menu */}
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            {/* Theme toggle */}
                            <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
                                <IconButton color="inherit" onClick={handleToggleDarkMode}>
                                    {darkMode ? <LightModeIcon/> : <DarkModeIcon/>}
                                </IconButton>
                            </Tooltip>
                            <Box>
                                <Tooltip title="Account settings">
                                    <IconButton
                                        size="large"
                                        aria-label="account of current user"
                                        aria-controls="menu-appbar"
                                        aria-haspopup="true"
                                        onClick={handleMenuOpen}
                                        color="inherit"
                                    >
                                        {userInfo && userInfo.picture ? (
                                            <Avatar
                                                src={userInfo.picture}
                                                alt={userInfo.name || 'User'}
                                                sx={{width: 32, height: 32}}
                                            />
                                        ) : (
                                            <Avatar sx={{width: 32, height: 32, bgcolor: 'secondary.main'}}>
                                                {getInitial()}
                                            </Avatar>
                                        )}
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    id="menu-appbar"
                                    anchorEl={anchorEl}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    PaperProps={{
                                        elevation: 3,
                                        sx: {
                                            overflow: 'visible',
                                            filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.1))',
                                            mt: 1.5,
                                            borderRadius: 1,
                                            minWidth: 180,
                                        },
                                    }}
                                >
                                    {userInfo && (
                                        <MenuItem sx={{pointerEvents: 'none', opacity: 0.8}}>
                                            <Avatar
                                                src={userInfo.picture}
                                                alt={userInfo.name || 'User'}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    mr: 1
                                                }}
                                            />
                                            <ListItemText
                                                primary={userInfo.name}
                                                secondary={userInfo.email}
                                                primaryTypographyProps={{
                                                    noWrap: true,
                                                    fontWeight: 'normal'
                                                }}
                                                secondaryTypographyProps={{
                                                    noWrap: true,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={handleEditProfile}>
                                        <EditIcon sx={{mr: 1}}/> Edit Profile
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>
                                        <LogoutIcon sx={{mr: 1}}/> Logout
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                    </Toolbar>
                </Container>
            </MuiAppBar>

            {/* Mobile drawer */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                {drawerContent}
            </Drawer>

            {/* Onboarding Modal */}
            {showOnboarding && backendUser && (
                <UserSetup
                    accessToken={accessToken}
                    user={backendUser}
                    onUserUpdated={handleOnboardingComplete}
                    showGreeting={true}
                    title="Welcome to DuoQuanto!"
                    submitButtonText="Complete Setup"
                    isOnboarding={true}
                />
            )}

            {/* Profile Edit Modal */}
            {showProfileEdit && backendUser && (
                <UserSetup
                    accessToken={accessToken}
                    user={backendUser}
                    onUserUpdated={handleProfileUpdateComplete}
                    showGreeting={false}
                    title="Edit Profile"
                    submitButtonText="Save Changes"
                    isOnboarding={false}
                />
            )}

            {/* Access Denied Backdrop */}
            <Backdrop
                open={showAccessDenied}
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Paper
                    sx={{
                        p: 4,
                        maxWidth: 500,
                        textAlign: 'center',
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                    }}
                >
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                        Access Denied
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        This application is only available to HdM Stuttgart members. Please use your @hdm-stuttgart.de email address.
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        You will be signed out in {countdown} seconds
                    </Typography>
                </Paper>
            </Backdrop>
        </>
    );
};

export default AppBar;